import { useState, useEffect, useCallback } from 'react';
import { builderApi } from '../api';
import { fmtDate, DateInput } from '../utils';

const EMPTY = {
  project_id: '', project_name: '', material_id: '', material_name: '', unit: '',
  quantity: '', issue_date: new Date().toISOString().slice(0,10),
  site_location: '', issued_by: '', notes: '',
};

export default function BuilderIssues({ user }) {
  const isOwner = user?.role === 'role_owner';
  const [issues, setIssues] = useState([]);
  const [projects, setProjects] = useState([]);
  const [projectMaterials, setProjectMaterials] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [showForm, setShowForm] = useState(false);
  const [filterProject, setFilterProject] = useState('');
  const [loading, setLoading] = useState(true);
  const [matLoading, setMatLoading] = useState(false);
  const [err, setErr] = useState('');
  const [fe, setFe] = useState({});

  const ErrBanner = ({ msg }) => msg ? (
    <div style={{ background: '#fff5f5', border: '1px solid #feb2b2', borderRadius: 8,
      padding: '10px 14px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ fontSize: 18 }}>⚠️</span>
      <span style={{ color: '#c53030', fontSize: 13, fontWeight: 600, flex: 1 }}>{msg}</span>
      <button onClick={() => setErr('')} style={{ background: 'none', border: 'none', color: '#c53030', cursor: 'pointer', fontSize: 16, padding: 0 }}>✕</button>
    </div>
  ) : null;

  const inv = k => fe[k] ? { borderColor: '#e53e3e', background: '#fff5f5', boxShadow: '0 0 0 3px rgba(229,62,62,.15)' } : {};

  const loadIssues = useCallback(() =>
    builderApi.getIssues(filterProject || undefined).then(setIssues).catch(e => setErr(e.message)),
  [filterProject]);

  useEffect(() => {
    Promise.all([builderApi.getProjects(), loadIssues()])
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  const pickProject = async (id) => {
    const p = projects.find(p => String(p.id) === String(id));
    setForm(f => ({ ...f, project_id: id, project_name: p?.name || '', material_id: '', material_name: '', unit: '', quantity: '' }));
    setProjectMaterials([]);
    if (!id) return;
    setMatLoading(true);
    try {
      const mats = await builderApi.getProjectMaterials(id);
      setProjectMaterials(mats);
    } catch (e) {
      setErr(e.message);
    } finally {
      setMatLoading(false);
    }
  };

  const pickMaterial = (id) => {
    const m = projectMaterials.find(m => String(m.material_id) === String(id));
    setForm(f => ({ ...f, material_id: id, material_name: m?.material_name || '', unit: m?.unit || '', quantity: '' }));
  };

  const availableFor = (matId) => {
    const m = projectMaterials.find(m => String(m.material_id) === String(matId));
    return m ? Number(m.available_qty) : 0;
  };

  const save = async () => {
    const errors = {};
    if (!form.project_id)  errors.project_id  = true;
    if (!form.material_id) errors.material_id = true;
    if (!form.quantity || Number(form.quantity) <= 0) errors.quantity = true;
    if (!form.issue_date)  errors.issue_date  = true;
    setFe(errors);
    if (Object.keys(errors).length > 0) { setErr('Please fill in all required fields (marked *)'); return; }
    try {
      await builderApi.createIssue(form);
      setShowForm(false);
      setForm(EMPTY);
      setProjectMaterials([]);
      setFe({});
      loadIssues();
    } catch (e) { setErr(e.message); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this issue record? Quantity will be restored to inventory.')) return;
    try { await builderApi.deleteIssue(id); loadIssues(); } catch (e) { setErr(e.message); }
  };

  const initProjects = (list) => { setProjects(list); };
  useEffect(() => { builderApi.getProjects().then(initProjects); }, []); // eslint-disable-line

  const fmtQty = n => Number(n).toLocaleString('en-IN', { maximumFractionDigits: 3 });

  return (
    <div className="bld-card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 18, color: 'var(--text-primary)' }}>Issue to Site</h2>
          <p style={{ margin: '4px 0 0', fontSize: 12, color: '#718096' }}>
            Record materials issued from a project's stock for site consumption
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => { setShowForm(s => !s); setForm(EMPTY); setProjectMaterials([]); }}>
          + Issue to Site
        </button>
      </div>

      <ErrBanner msg={err} />

      {showForm && (
        <div style={{ background: 'var(--bg-secondary)', borderRadius: 8, padding: 16, marginBottom: 16, border: '1px solid var(--border-color)' }}>
          <div style={{ marginBottom: 12, fontSize: 13, fontWeight: 600, color: '#4a5568' }}>
            Select the project first to see its available materials
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            {/* Row 1 */}
            <div>
              <label className="form-label">Project (Source) *</label>
              <select className="form-input" style={inv('project_id')} value={form.project_id}
                onChange={e => { pickProject(e.target.value); if (fe.project_id) setFe(p => ({ ...p, project_id: false })); }}>
                <option value="">Select project</option>
                {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              {fe.project_id && <div style={{ color: '#e53e3e', fontSize: 11, marginTop: 3 }}>Required</div>}
            </div>
            <div>
              <label className="form-label">
                Material *
                {matLoading && <span style={{ color: '#718096', fontWeight: 400 }}> Loading…</span>}
                {form.project_id && !matLoading && projectMaterials.length === 0 && (
                  <span style={{ color: '#e53e3e', fontWeight: 400 }}> No materials available for this project</span>
                )}
              </label>
              <select className="form-input" style={inv('material_id')} value={form.material_id}
                disabled={!form.project_id || matLoading}
                onChange={e => { pickMaterial(e.target.value); if (fe.material_id) setFe(p => ({ ...p, material_id: false })); }}>
                <option value="">{form.project_id ? 'Select material' : 'Select project first'}</option>
                {projectMaterials.map(m => (
                  <option key={m.material_id} value={m.material_id}>
                    {m.material_name} (Avail: {Number(m.available_qty).toFixed(2)} {m.unit})
                  </option>
                ))}
              </select>
              {fe.material_id && <div style={{ color: '#e53e3e', fontSize: 11, marginTop: 3 }}>Required</div>}
            </div>
            <div>
              <label className="form-label">
                Quantity *
                {form.material_id && (
                  <span style={{ color: '#718096', fontWeight: 400 }}>
                    {' '}(max {availableFor(form.material_id).toFixed(2)} {form.unit})
                  </span>
                )}
              </label>
              <input className="form-input" style={inv('quantity')} type="number" min="0.001" step="0.001"
                max={availableFor(form.material_id) || undefined}
                value={form.quantity} placeholder="0"
                onChange={e => { setForm(f => ({ ...f, quantity: e.target.value })); if (fe.quantity) setFe(p => ({ ...p, quantity: false })); }} />
              {fe.quantity && <div style={{ color: '#e53e3e', fontSize: 11, marginTop: 3 }}>Enter a valid quantity</div>}
            </div>

            {/* Row 2 */}
            <div>
              <label className="form-label">Issue Date *</label>
              <DateInput className="form-input" style={inv('issue_date')} value={form.issue_date}
                onChange={e => { setForm(f => ({ ...f, issue_date: e.target.value })); if (fe.issue_date) setFe(p => ({ ...p, issue_date: false })); }} />
              {fe.issue_date && <div style={{ color: '#e53e3e', fontSize: 11, marginTop: 3 }}>Required</div>}
            </div>
            <div>
              <label className="form-label">Site / Location</label>
              <input className="form-input" value={form.site_location}
                onChange={e => setForm(f => ({ ...f, site_location: e.target.value }))}
                placeholder="e.g. Block A – Column Foundation" />
            </div>
            <div>
              <label className="form-label">Issued By</label>
              <input className="form-input" value={form.issued_by}
                onChange={e => setForm(f => ({ ...f, issued_by: e.target.value }))}
                placeholder="Name / designation" />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label className="form-label">Notes</label>
              <input className="form-input" value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Optional remarks" />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button className="btn btn-primary" onClick={save}>Confirm Issue</button>
            <button className="btn btn-outline" onClick={() => { setShowForm(false); setProjectMaterials([]); }}>Cancel</button>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <select className="form-input" style={{ maxWidth: 240 }} value={filterProject}
          onChange={e => setFilterProject(e.target.value)}>
          <option value="">All Projects</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <button className="btn btn-outline" style={{ fontSize: 12 }} onClick={loadIssues}>Filter</button>
      </div>

      {loading ? <div style={{ color: '#718096', fontSize: 13 }}>Loading…</div> : (
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table" style={{ width: '100%', tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: '10%' }} />
              <col style={{ width: '16%' }} />
              <col style={{ width: '16%' }} />
              <col style={{ width: '11%' }} />
              <col style={{ width: '15%' }} />
              <col style={{ width: '14%' }} />
              <col style={{ width: '12%' }} />
              <col style={{ width: '6%' }} />
            </colgroup>
            <thead>
              <tr>
                <th>Date</th>
                <th>Project</th>
                <th>Material</th>
                <th style={{ textAlign: 'right' }}>Qty</th>
                <th>Site / Location</th>
                <th>Issued By</th>
                <th>Notes</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {issues.length === 0 && (
                <tr><td colSpan={8} style={{ textAlign: 'center', color: '#718096', padding: 24 }}>
                  No site issues recorded
                </td></tr>
              )}
              {issues.map(iss => (
                <tr key={iss.id}>
                  <td style={{ fontSize: 12 }}>{fmtDate(iss.issue_date)}</td>
                  <td style={{ fontSize: 13 }}>{iss.project_name || '—'}</td>
                  <td style={{ fontWeight: 500 }}>{iss.material_name}</td>
                  <td style={{ textAlign: 'right', fontWeight: 600, color: '#d69e2e' }}>
                    {fmtQty(iss.quantity)} <span style={{ fontWeight: 400, fontSize: 11, color: '#718096' }}>{iss.unit}</span>
                  </td>
                  <td style={{ fontSize: 12 }}>{iss.site_location || '—'}</td>
                  <td style={{ fontSize: 12 }}>{iss.issued_by || '—'}</td>
                  <td style={{ fontSize: 12, color: '#718096' }}>{iss.notes || '—'}</td>
                  <td>
                    {isOwner && (
                      <button className="btn" style={{ background: '#fed7d7', color: '#c53030', padding: '3px 10px', fontSize: 12 }}
                        onClick={() => del(iss.id)}>Del</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
